import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchGalleriesSummary, deleteGallery, isAdmin, getCurrentOwnerId, getCoverPublicUrl } from '../lib/api'
import AuthImage from '../components/AuthImage'
import CreateGalleryModal from '../components/CreateGalleryModal'
import GalleryCreateForm from '../components/GalleryCreateForm'
import GalleryEditForm from '../components/GalleryEditForm'
import QrEditorModal, { GALLERY_BASE_URL } from '../components/QrEditorModal'
import ManageGalleryAccessModal from '../components/gallery/ManageGalleryAccessModal'
import type { GallerySummary } from '../lib/types'
import LoadingDots from '../components/LoadingDots'

function GalleryActionsMenu({
  gallery,
  deletingId,
  onQr,
  onEdit,
  onShare,
  onDelete,
}: {
  gallery: GallerySummary
  deletingId: string | null
  onQr: () => void
  onEdit: () => void
  onShare: () => void
  onDelete: (e: React.MouseEvent) => void
}) {
  const currentIsOwner = gallery.isOwner
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('touchstart', handler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('touchstart', handler)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        className="p-2 text-gray-500 hover:text-white transition-colors rounded"
        aria-label="Handlinger"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
          <circle cx="5" cy="12" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="19" cy="12" r="1.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[10rem] rounded-lg border border-gray-700 bg-gray-900 shadow-xl py-1">
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onQr() }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
            </svg>
            QR-kode
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onShare() }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
            </svg>
            Del
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onEdit() }}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
            </svg>
            Rediger
          </button>
          {currentIsOwner && (
            <>
              <div className="border-t border-gray-700 my-1" />
              <button
                onClick={(e) => { setOpen(false); onDelete(e) }}
                disabled={deletingId === gallery.id}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-gray-800 hover:text-red-300 disabled:opacity-40 transition-colors"
              >
                {deletingId === gallery.id ? (
                  <LoadingDots />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                )}
                Slett galleri
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default function Manage() {
  const navigate = useNavigate()
  const [galleries, setGalleries] = useState<GallerySummary[]>([])
  const [coverUrls, setCoverUrls] = useState<Map<string, string>>(new Map())
  const [admin, setAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [qrGallery, setQrGallery] = useState<{ shortCode: string; name: string } | null>(null)
  const [editGalleryId, setEditGalleryId] = useState<string | null>(null)
  const [shareGalleryId, setShareGalleryId] = useState<{ id: string; name: string } | null>(null)

  useEffect(() => {
    Promise.all([isAdmin(), getCurrentOwnerId()])
      .then(([adminResult, ownerId]) => {
        setAdmin(adminResult)
        return fetchGalleriesSummary({ isAdmin: adminResult, ownerId: ownerId ?? undefined })
      })
      .then((gs) => {
        setGalleries(gs)
        const entries = gs
          .filter((g) => g.landingImageUrl)
          .map((g) => ({ id: g.id, url: getCoverPublicUrl(g.landingImageUrl) }))
        setCoverUrls(new Map(entries.map((e) => [e.id, e.url])))
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  const handleDelete = async (e: React.MouseEvent, galleryId: string, galleryName: string) => {
    e.stopPropagation()
    if (!window.confirm(`Slett "${galleryName || 'Ikke konfigurert'}"? Dette kan ikke angres.`)) return
    setDeletingId(galleryId)
    try {
      await deleteGallery(galleryId)
      setGalleries((prev) => prev.filter((g) => g.id !== galleryId))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setDeletingId(null)
    }
  }

  const handleCreateSuccess = (_galleryId: string, shortCode: string) => {
    setShowCreateModal(false)
    navigate(`/${shortCode}`)
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-6xl mx-auto px-4 py-4">

        {/* Header */}
        <div className="flex items-center justify-start mb-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 text-white/40 hover:text-white/80 transition-colors duration-200 p-2"
            aria-label="Legg til galleri"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            <span className="text-sm font-medium">Legg til</span>
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-red-900 border border-red-700 p-4 text-red-200 text-sm mb-6">
            {error}
          </div>
        )}

        {/* Mobile card list (hidden on md+) */}
        <div className="md:hidden flex flex-col gap-3">

          {loading && [0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl bg-gray-900 border border-gray-800 p-4 flex items-center gap-3">
              <div className="w-16 h-11 rounded bg-gray-700 animate-pulse flex-shrink-0" />
              <div className="flex-1 flex flex-col gap-2">
                <div className="h-4 w-36 rounded bg-gray-700 animate-pulse" />
                <div className="h-3 w-24 rounded bg-gray-700 animate-pulse" />
              </div>
            </div>
          ))}

          {!loading && galleries.length === 0 && (
            <div className="rounded-xl bg-gray-900 border border-gray-800 px-4 py-12 text-center text-gray-500 text-sm">
              Ingen gallerier ennå. Klikk "Legg til galleri" for å opprette et.
            </div>
          )}

          {!loading && galleries.map((gallery) => (
            <div
              key={gallery.id}
              onClick={() => navigate(`/${gallery.shortCode}`)}
              className="rounded-xl bg-gray-900 border border-gray-800 p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-800 transition-colors"
            >
              {/* Thumbnail */}
              <div className="relative w-16 h-11 rounded overflow-hidden bg-gray-700 flex-shrink-0">
                {coverUrls.get(gallery.id) ? (
                  <AuthImage
                    src={coverUrls.get(gallery.id)!}
                    alt={gallery.name}
                    className="absolute inset-0"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Name + meta */}
              <div className="flex-1 min-w-0">
                {gallery.name ? (
                  <p className="font-medium text-white truncate">{gallery.name}</p>
                ) : (
                  <p className="text-gray-500 italic text-sm">Ikke konfigurert</p>
                )}
                <p className="text-xs text-gray-500 mt-0.5 font-mono">
                  {gallery.collectionCount} saml. · {gallery.itemCount} bilder
                  {(gallery.locationLat !== 0 || gallery.locationLng !== 0) && (
                    <> · {gallery.locationLat.toFixed(4)}, {gallery.locationLng.toFixed(4)}</>
                  )}
                  {admin && gallery.ownerName && (
                    <> · {gallery.ownerName}</>
                  )}
                </p>
              </div>

              {/* 3-dot menu */}
              <div className="flex-shrink-0">
                <GalleryActionsMenu
                  gallery={gallery}
                  deletingId={deletingId}
                  onQr={() => setQrGallery({ shortCode: gallery.shortCode, name: gallery.name })}
                  onEdit={() => setEditGalleryId(gallery.id)}
                   onShare={() => setShareGalleryId({ id: gallery.id, name: gallery.name })}
                   onDelete={(e) => handleDelete(e, gallery.id, gallery.name)}
                 />
               </div>
             </div>
           ))}
         </div>

        {/* Desktop table skeleton (hidden below md) */}
        {loading && (
          <div className="hidden md:block rounded-xl overflow-hidden border border-gray-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-900 text-gray-400 text-left">
                  <th className="px-4 py-3 font-medium w-16">Bilde</th>
                  <th className="px-4 py-3 font-medium">Navn</th>
                  <th className="px-4 py-3 font-medium text-center">Samlinger</th>
                  <th className="px-4 py-3 font-medium text-center">Bilder</th>
                  <th className="px-4 py-3 w-12" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {[0, 1, 2].map((i) => (
                  <tr key={i} className="bg-gray-900">
                    <td className="px-4 py-3"><div className="w-12 h-8 rounded bg-gray-700 animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-40 rounded bg-gray-700 animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-6 rounded bg-gray-700 animate-pulse mx-auto" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-6 rounded bg-gray-700 animate-pulse mx-auto" /></td>
                    <td className="px-4 py-3" />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Desktop table (hidden below md) */}
        {!loading && (
        <div className="hidden md:block rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-900 text-gray-400 text-left">
                <th className="px-4 py-3 font-medium w-16">Bilde</th>
                <th className="px-4 py-3 font-medium">Navn</th>
                {admin && <th className="px-4 py-3 font-medium hidden md:table-cell">Eier</th>}
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Sted</th>
                <th className="px-4 py-3 font-medium text-center">Samlinger</th>
                <th className="px-4 py-3 font-medium text-center">Bilder</th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">

              {galleries.length === 0 && (
                <tr className="bg-gray-900">
                  <td colSpan={admin ? 7 : 6} className="px-4 py-12 text-center text-gray-500">
                    Ingen gallerier ennå. Klikk "Legg til galleri" for å opprette et.
                  </td>
                </tr>
              )}

              {galleries.map((gallery) => (
                <tr
                  key={gallery.id}
                  onClick={() => navigate(`/${gallery.shortCode}`)}
                  className="bg-gray-900 hover:bg-gray-800 cursor-pointer transition-colors duration-150"
                >
                  {/* Cover thumbnail */}
                  <td className="px-4 py-3">
                    <div className="relative w-12 h-8 rounded overflow-hidden bg-gray-700 flex-shrink-0">
                      {coverUrls.get(gallery.id) ? (
                        <AuthImage
                          src={coverUrls.get(gallery.id)!}
                          alt={gallery.name}
                          className="absolute inset-0"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-500">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Name */}
                  <td className="px-4 py-3">
                    {gallery.name ? (
                      <span className="font-medium text-white">{gallery.name}</span>
                    ) : (
                      <span className="text-gray-500 italic">Ikke konfigurert</span>
                    )}
                  </td>

                  {/* Owner — admins only */}
                  {admin && (
                    <td className="px-4 py-3 text-gray-400 hidden md:table-cell">
                      {gallery.ownerName ?? <span className="text-gray-600">—</span>}
                    </td>
                  )}

                  {/* Location */}
                  <td className="px-4 py-3 text-gray-400 hidden lg:table-cell font-mono text-xs">
                    {gallery.locationLat !== 0 || gallery.locationLng !== 0 ? (
                      `${gallery.locationLat.toFixed(4)}, ${gallery.locationLng.toFixed(4)}`
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>

                  {/* Collections */}
                  <td className="px-4 py-3 text-center text-gray-300">
                    {gallery.collectionCount}
                  </td>

                  {/* Items */}
                  <td className="px-4 py-3 text-center text-gray-300">
                    {gallery.itemCount}
                  </td>

                  {/* Actions: 3-dot menu */}
                  <td className="px-4 py-3">
                    <GalleryActionsMenu
                      gallery={gallery}
                      deletingId={deletingId}
                      onQr={() => setQrGallery({ shortCode: gallery.shortCode, name: gallery.name })}
                      onEdit={() => setEditGalleryId(gallery.id)}
                       onShare={() => setShareGalleryId({ id: gallery.id, name: gallery.name })}
                       onDelete={(e) => handleDelete(e, gallery.id, gallery.name)}
                     />
                   </td>
                 </tr>
               ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Create gallery modal */}
      {showCreateModal && (
        <CreateGalleryModal onClose={() => setShowCreateModal(false)}>
          <GalleryCreateForm
            onDone={({ id, shortCode }) => { if (shortCode) handleCreateSuccess(id, shortCode) }}
            onClose={() => setShowCreateModal(false)}
          />
        </CreateGalleryModal>
      )}

      {/* Edit gallery modal */}
      {editGalleryId && (
        <CreateGalleryModal onClose={() => setEditGalleryId(null)}>
          <GalleryEditForm
            galleryId={editGalleryId}
            onDone={() => {
              setEditGalleryId(null)
              // Reload the gallery list to reflect edits
              Promise.all([isAdmin(), getCurrentOwnerId()])
                .then(([adminResult, ownerId]) =>
                  fetchGalleriesSummary({ isAdmin: adminResult, ownerId: ownerId ?? undefined })
                )
                .then((gs) => {
                  setGalleries(gs)
                  const entries = gs
                    .filter((g) => g.landingImageUrl)
                    .map((g) => ({ id: g.id, url: getCoverPublicUrl(g.landingImageUrl) }))
                  setCoverUrls(new Map(entries.map((e) => [e.id, e.url])))
                })
                .catch((err: Error) => setError(err.message))
            }}
            onClose={() => setEditGalleryId(null)}
          />
        </CreateGalleryModal>
      )}

      {/* QR editor modal */}
      {qrGallery && (
        <QrEditorModal
          galleryUrl={`${GALLERY_BASE_URL}/${qrGallery.shortCode}`}
          galleryName={qrGallery.name}
          onClose={() => setQrGallery(null)}
        />
      )}

      {/* Share access modal */}
      {shareGalleryId && (
        <ManageGalleryAccessModal
          galleryId={shareGalleryId.id}
          galleryName={shareGalleryId.name}
          onClose={() => setShareGalleryId(null)}
        />
      )}
    </div>
  )
}
